import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import Tooltip from '@components/Tooltip';
import { CHANNEL, STATUS } from '@constants/channels';
import { getChannelIcon } from '@helpers/channels';
import { useTranslation } from './useTranslation';
import { useLocations } from './useLocations';

export const useChannels = () => {
    const channels = useSelector(state => state.channels.data);
    const { locationsMap } = useLocations();
    const { t } = useTranslation();

    const channelsMap = useMemo(
        () =>
            channels.reduce((acc, channel) => {
                acc[channel.id] = channel;
                return acc;
            }, {}),
        [channels],
    );

    const getChannelsByBrand = (brandId, filterCb) => {
        const hasFilterCb = typeof filterCb === 'function';
        const channelsByBrand = channels.filter(channel => channel.brandId === brandId);
        const filteredChannels = hasFilterCb ? channelsByBrand.filter(filterCb) : channelsByBrand;
        return filteredChannels.map(c => c.id);
    };

    const getChannelsByLocationId = (locationId, filterCb) => {
        const hasFilterCb = typeof filterCb === 'function';
        const location = locationsMap[locationId];
        if (!location) return [];
        const channelsByLocation = location.channels?.map(channelId => channelsMap[channelId]).filter(Boolean);
        const filteredChannels = hasFilterCb ? channelsByLocation.filter(filterCb) : channelsByLocation;
        return filteredChannels.map(c => c.id);
    };

    const getChannelsByLocationsIds = (locationsIds, filterCb) => {
        const hasFilterCb = typeof filterCb === 'function';
        const channelsIdsByLocationsIds = [
            ...new Set(locationsIds.map(locationId => locationsMap[locationId]?.channels || []).flat()),
        ];
        const channelsByLocationsIds = channelsIdsByLocationsIds
            .map(channelId => channelsMap[channelId])
            .filter(Boolean);
        const filteredChannels = hasFilterCb ? channelsByLocationsIds.filter(filterCb) : channelsByLocationsIds;
        return filteredChannels.map(c => c.id);
    };

    const channelIsAssignedToBrandOrLocation = (channelId, brandId, locationId) => {
        const channel = channelsMap[channelId];
        const brandOfLocation = locationsMap[locationId]?.brand?.id;
        return (
            channel?.brandId === brandId || channel?.locationId === locationId || channel?.brandId === brandOfLocation
        );
    };

    const getChannelsByLocationIdStrict = locationId => {
        if (!locationId) return [];
        return channels.filter(channel => channel.locationId === locationId).map(c => c.id);
    };

    const FILTER = {
        in: false,
        out: false,
        status: true,
        contact: null,
        smsTwoWay: false,
        withArchivedMultiselect: false,
        withArchivedSelect: false,
        withDisconnected: false,
        withPluspointEmail: false,
        withDisabledAgents: false,
        currentAgent: null,
    };

    const createChannelLabel = params => (
        <span
            style={{
                display: 'grid',
                alignItems: 'center',
                gridGap: '8px',
                gridTemplateColumns: '16px calc(100% - 24px)',
            }}
        >
            {getChannelIcon(params.type)}{' '}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{params.name}</span>
        </span>
    );
    /**
     * @param {Object} filter - Object with in and out boolean values for filtering incoming and outgoing channels and status
     *                          for filtering by status SUCCESS and contact for filtering by email and phoneNumber
     * @param {Array} availableChannels - Array of channel ids.
     * @param {Array} channels - Array of channels.
     * @returns {ReactNode} Returns filtered channel option for multiselect.
     */
    const getChannelOptions = (filter, availableChannels) => {
        const updatedFilter = { ...FILTER, ...filter };
        const options = availableChannels
            .filter(channelId => {
                const channel = channelsMap[channelId];
                if (!channel) return false;
                if (channel?.incoming !== updatedFilter.in && channel?.outgoing !== updatedFilter.out) {
                    return false;
                }
                if (
                    updatedFilter.status &&
                    channel?.status !== STATUS.SUCCESS &&
                    (channel?.status !== STATUS.DISCONNECTED || !updatedFilter.withDisconnected)
                ) {
                    return false;
                }
                if (
                    updatedFilter?.contact &&
                    ((channel?.type === CHANNEL.EMAIL && !updatedFilter?.contact?.email) ||
                        ((channel?.type === CHANNEL.SMS ||
                            channel?.type === CHANNEL.WHATSAPP ||
                            channel?.type === CHANNEL.WHATSAPP_BUSINESS) &&
                            !updatedFilter?.contact?.phoneNumber))
                ) {
                    return false;
                }
                //to filter out twilio two way sms channels, only the ones don't have senderId
                if (updatedFilter?.smsTwoWay && channel?.type === CHANNEL.SMS && channel?.smsParameters?.senderId) {
                    return false;
                }
                return true;
            })
            ?.map(channelId => {
                const channel = channelsMap[channelId];
                const isDisabledByAgent =
                    updatedFilter.withDisabledAgents &&
                    channel?.agentId &&
                    channel?.agentId !== updatedFilter.currentAgent;
                return {
                    value: channel?.id,
                    disabled: isDisabledByAgent,
                    label: isDisabledByAgent ? (
                        <Tooltip disableInteractive title={t('Agents.channelTakenByAgent')} placement="top">
                            {createChannelLabel({ name: channel?.name, type: channel?.type })}
                        </Tooltip>
                    ) : (
                        createChannelLabel({ name: channel?.name, type: channel?.type })
                    ),
                    name: channel?.name,
                    type: channel?.type,
                };
            });

        return [
            ...options,
            ...(updatedFilter.withArchivedMultiselect || updatedFilter.withArchivedSelect
                ? [
                      {
                          value: updatedFilter.withArchivedMultiselect ? CHANNEL.ARCHIVED : 'ARCHIVED',
                          name: t('common.archived'),
                          label: createChannelLabel({
                              name: t('common.archived'),
                              type: CHANNEL.ARCHIVED,
                          }),
                      },
                  ]
                : []),
            ...(updatedFilter.withPluspointEmail
                ? [
                      {
                          value: 'PLUSPOINT_EMAIL',
                          name: 'Pluspoint Email',
                          label: createChannelLabel({
                              name: 'Pluspoint Email',
                              type: CHANNEL.EMAIL,
                          }),
                      },
                  ]
                : []),
        ];
    };

    const checkTwoWaySms = channel =>
        channel.type === CHANNEL.SMS && !!channel.smsParameters && !channel.smsParameters?.senderId;

    const checkWhatsAppStatus = channel =>
        channel.type === CHANNEL.WHATSAPP &&
        (channel.status === STATUS.SUCCESS || channel.status === STATUS.DISCONNECTED);

    const checkTwoWayLiveChat = channel =>
        channel.type === CHANNEL.LIVE_CHAT && channel.liveChatParameters?.notifyAboutMissingConversations;

    return {
        getChannelOptions,
        getChannelsByBrand,
        getChannelsByLocationId,
        getChannelsByLocationsIds,
        getChannelsByLocationIdStrict,
        channelIsAssignedToBrandOrLocation,
        checkTwoWaySms,
        checkWhatsAppStatus,
        checkTwoWayLiveChat,
    };
};
